import { Button } from 'components/buttons'
import { FC, ReactNode, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'

interface Props {
  children?: ReactNode
  className?: string
  fields: JSX.Element
  isSaving: boolean
  onCancel: () => void
  onSave: () => void
  saveButtonText?: string
  saveDisabled?: boolean
}

// This is designed to be used in conjunction with the Modal form variant
// (e.g. could be passed as its `content` prop)
export const ModalForm: FC<Props> = ({
  children,
  className,
  fields,
  isSaving,
  onCancel,
  onSave,
  saveButtonText,
  saveDisabled = false,
}) => {
  const ref = useRef<HTMLFormElement>(null)
  const { t } = useTranslation()

  const save = () => {
    if (ref.current?.reportValidity()) {
      onSave()
    }
  }

  return (
    <form
      ref={ref}
      className={twMerge('flex flex-col', className)}
      onSubmit={(event) => event.preventDefault()}
    >
      <div className="p-6 empty:hidden">{children}</div>
      <div className="flex flex-col p-6 pt-0 gap-4 grow">{fields}</div>
      <div className="flex justify-end w-full px-6 py-4 border-t border-neutral-300">
        <Button
          type="button"
          className="mr-2"
          disabled={isSaving}
          loading={isSaving}
          onClick={onCancel}
          title={t('common.cancel')}
          variant="outlined"
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="button"
          disabled={isSaving || saveDisabled}
          onClick={save}
          loading={isSaving}
          title={t('common.save')}
        >
          {saveButtonText || t('common.save')}
        </Button>
      </div>
    </form>
  )
}
